import type { Lens } from '~/types/leLens';

import { isLensDocument } from './document/types';

export type DefinitionChange = { field: string; before: string; after: string };

export function lensDefinitionChanges(before: unknown, after: unknown, field = ''): Array<DefinitionChange> {
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  if (before && after && typeof before === 'object' && typeof after === 'object') {
    const left = before as Record<string, unknown>;
    const right = after as Record<string, unknown>;
    return Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).flatMap((key) =>
      lensDefinitionChanges(left[key], right[key], field ? `${field}.${key}` : key),
    );
  }
  return [
    {
      field,
      before: before === undefined ? '—' : typeof before === 'string' ? before : JSON.stringify(before),
      after: after === undefined ? '—' : typeof after === 'string' ? after : JSON.stringify(after),
    },
  ];
}

export function editableLensDefinition(lens: Pick<Lens, 'title' | 'description' | 'spec'>) {
  if (isLensDocument(lens.spec)) return { title: lens.title, description: lens.description, spec: lens.spec };
  return {
    title: lens.title,
    description: lens.description,
    spec: {
      ...lens.spec,
      // Hydration returns [] for a Lens with no datasets; raw draft definitions may omit it.
      // Compare the same empty collection without changing either authoritative definition.
      datasets: (lens.spec.datasets ?? []).map(
        ({ version, scope, runAs, residence, deletedAt, ...definition }) => definition,
      ),
    },
  };
}

export function lensChangeLabel(field: string): string {
  const parts = field.replace(/^spec\./, '').split('.');
  const groups: Record<string, string> = { cards: 'Card', datasets: 'Dataset', sections: 'Section', filters: 'Filter' };
  const group = groups[parts[0]];
  const prefix = group && /^\d+$/.test(parts[1] ?? '') ? `${group} ${Number(parts.splice(0, 2)[1]) + 1}` : '';
  const label = parts
    .filter((part) => part !== 'config')
    .map((part) => part.replace(/([a-z])([A-Z])/g, '$1 $2'))
    .join(' · ');
  return [prefix, label.charAt(0).toUpperCase() + label.slice(1)].filter(Boolean).join(' · ');
}
